/**
 * Lockpick minigame — a cursor `>` bounces left-right across `[----X----]`.
 * Player taps when cursor is on `X`. Multiple attempts within time limit.
 * Target repositions after each attempt.
 */

import type { Minigame, MinigameState } from "./manager";

/** Time limit in milliseconds. */
const TIME_LIMIT = 6000;

/** Cursor speed: full bar traversal per second. */
const CURSOR_SPEED = 1.5 / 1000; // 1.5 full widths per second → fast enough to be challenging

/** Hit tolerance: how close cursor must be to target (fraction of bar). */
const HIT_TOLERANCE = 0.08;

/** Internal bar width (characters between brackets). */
const BAR_WIDTH = 20;

export class Lockpick implements Minigame {
  readonly id = "lockpick";
  readonly name = "Lockpick";

  init(): MinigameState {
    return {
      elapsed: 0,
      cursorPos: 0,
      cursorDir: 1,
      targetPos: this.randomTarget(),
      hits: 0,
      attempts: 0,
      barWidth: BAR_WIDTH,
    };
  }

  tick(dt: number, state: MinigameState): MinigameState {
    const elapsed = (state.elapsed as number) + dt;
    let cursorPos = (state.cursorPos as number) + (state.cursorDir as number) * CURSOR_SPEED * dt;
    let cursorDir = state.cursorDir as number;

    // Bounce at boundaries
    if (cursorPos >= 1) {
      cursorPos = 1 - (cursorPos - 1);
      cursorDir = -1;
    } else if (cursorPos <= 0) {
      cursorPos = -cursorPos;
      cursorDir = 1;
    }

    // Clamp just in case
    cursorPos = Math.max(0, Math.min(1, cursorPos));

    return { ...state, elapsed, cursorPos, cursorDir };
  }

  onInput(state: MinigameState): MinigameState {
    const cursorPos = state.cursorPos as number;
    const targetPos = state.targetPos as number;
    const hits = state.hits as number;
    const attempts = (state.attempts as number) + 1;

    const dist = Math.abs(cursorPos - targetPos);
    const isHit = dist <= HIT_TOLERANCE;

    return {
      ...state,
      hits: isHit ? hits + 1 : hits,
      attempts,
      targetPos: this.randomTarget(),
    };
  }

  isComplete(state: MinigameState): boolean {
    return (state.elapsed as number) >= TIME_LIMIT;
  }

  score(state: MinigameState): number {
    const attempts = state.attempts as number;
    const hits = state.hits as number;
    if (attempts === 0) return 0;
    return Math.min(Math.round((hits / attempts) * 100), 100);
  }

  render(width: number, height: number, state: MinigameState): string[] {
    const lines: string[] = [];
    const cursorPos = state.cursorPos as number;
    const targetPos = state.targetPos as number;
    const hits = state.hits as number;
    const attempts = state.attempts as number;
    const barW = state.barWidth as number;

    const midRow = Math.floor(height / 2);
    const barStart = Math.floor((width - barW - 2) / 2); // center the bar

    for (let r = 0; r < height; r++) {
      const chars = Array.from({ length: width }, () => " ");

      if (r === midRow) {
        // Draw the bar: [----X----]
        const bStart = Math.max(0, barStart);
        if (bStart < width) chars[bStart] = "[";
        if (bStart + barW + 1 < width) chars[bStart + barW + 1] = "]";

        for (let i = 1; i <= barW; i++) {
          const col = bStart + i;
          if (col >= width) break;
          chars[col] = "-";
        }

        // Target position
        const targetCol = bStart + 1 + Math.round(targetPos * (barW - 1));
        if (targetCol < width) chars[targetCol] = "X";

        // Cursor position (shown on the row above as well, but mark here)
        const cursorCol = bStart + 1 + Math.round(cursorPos * (barW - 1));
        if (cursorCol < width) chars[cursorCol] = ">";
      } else if (r === midRow + 2) {
        // Hit counter
        const label = `Hits: ${hits}/${attempts}`;
        const labelStart = Math.floor((width - label.length) / 2);
        for (let i = 0; i < label.length && labelStart + i < width; i++) {
          if (labelStart + i >= 0) chars[labelStart + i] = label[i];
        }
      }

      lines.push(chars.join(""));
    }

    return lines;
  }

  /** Generate a random target position in (0.1, 0.9) to avoid edges. */
  private randomTarget(): number {
    return 0.1 + Math.random() * 0.8;
  }
}
